import { useCallback, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";
import "@mdxeditor/editor/style.css";
import {
  MDXEditor,
  MDXEditorMethods,
  toolbarPlugin,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  linkPlugin,
  codeBlockPlugin,
  tablePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  imagePlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  CodeToggle,
  ListsToggle,
  InsertTable,
  InsertThematicBreak,
  Separator,
  BlockTypeSelect,
} from "@mdxeditor/editor";

type DocumentEditorProps = {
  value: string;
  onChange: (markdown: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
};

const DocumentCanvas = styled("div")({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  overflow: "auto",
  padding: "32px 48px",
  background: "linear-gradient(180deg, #cfd6df, #b7c0cb)",
});

const DocumentSurface = styled("div")({
  width: "100%",
  maxWidth: "820px",
  minHeight: "100%",
  background: "#fff",
  borderRadius: "18px",
  boxShadow: "0 32px 60px rgba(15, 23, 42, 0.35)",
  padding: "48px 56px 64px",
  position: "relative",
  '& .mdxeditor-toolbar': {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#fff',
    paddingBottom: '8px',
    marginBottom: '16px',
    border: 'none',
    borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
    borderRadius: 0,
  },
  '& .mdxeditor': {
    border: 'none',
    background: 'transparent',
  },
  '& .document-editor-content': {
    color: '#0f172a',
    minHeight: 'calc(var(--vh100) * 0.7)',
    padding: 0,
    fontSize: '11.5pt',
    lineHeight: 1.65,
    boxSizing: 'border-box',
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
      margin: '18px auto',
      borderRadius: '8px',
    },
  },
  '@media print': {
    width: '210mm',
    minHeight: '297mm',
    padding: '20mm 25mm',
    borderRadius: 0,
    boxShadow: 'none',
    '& .document-editor-content img': {
      maxWidth: '100%',
      height: 'auto',
    },
  },
});

const DocumentEditor = ({ value, onChange, readOnly = false, placeholder, className }: DocumentEditorProps) => {
    const editorRef = useRef<MDXEditorMethods | null>(null);

    const handleChange = useCallback(
      (markdown: string) => {
        onChange(markdown);
      },
      [onChange],
    );

    useEffect(() => {
      if (!editorRef.current) return;
      if (editorRef.current.getMarkdown() === value) return;
      editorRef.current.setMarkdown(value);
    }, [value]);

    return (
      <DocumentCanvas className={className}>
        <DocumentSurface>
          <MDXEditor
            ref={editorRef}
            markdown={value}
            onChange={handleChange}
            readOnly={readOnly}
            placeholder={placeholder}
            contentEditableClassName="document-editor-content"
            plugins={[
              toolbarPlugin({
                toolbarContents: () => (
                  <>
                    <UndoRedo />
                    <Separator />
                    <BoldItalicUnderlineToggles />
                    <BlockTypeSelect />
                    <CodeToggle />
                    <Separator />
                    <ListsToggle />
                    <InsertThematicBreak />
                    <InsertTable />
                  </>
                ),
              }),
              headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4, 5, 6] }),
              listsPlugin(),
              quotePlugin(),
              linkPlugin(),
              codeBlockPlugin(),
              tablePlugin(),
              thematicBreakPlugin(),
              markdownShortcutPlugin(),
              imagePlugin(),
            ]}
          />
        </DocumentSurface>
      </DocumentCanvas>
    );
};

DocumentEditor.displayName = "DocumentEditor";

export default DocumentEditor;
